// src/components/RicordamiAlle.js
// «🔔 Ricordamelo alle» su attività e sottoattività (utils/promemoriaOrari.js).
// Promemoria personale: lo vede solo chi lo imposta e arriva solo a lui.
import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { toLocalDateStr } from "../utils/dateUtils";
import { chiaveOrario, oreDisponibili, orarioValido, primaOraUtile, sceltePrimaDellaScadenza, testoOrario } from "../utils/promemoriaOrari";

const p2 = n => String(n).padStart(2, "0");

export default function RicordamiAlle({ collezione, item, projectId = null, taskIdDaAprire = null, disabilitato = false }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const [mio, setMio] = useState(null);       // promemoria già impostato
  const [aperto, setAperto] = useState(false);
  const [data, setData] = useState("");
  const [ora, setOra] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!uid || !item?.id) return;
    const q = query(collection(db, "promemoria"), where("uid", "==", uid), where("rifId", "==", item.id));
    return onSnapshot(q, s => setMio(s.empty ? null : { id: s.docs[0].id, ...s.docs[0].data() }), () => setMio(null));
  }, [uid, item?.id]);

  const apri = () => {
    const prima = primaOraUtile();
    const giorno = mio?.alle ? mio.alle.slice(0, 10) : (item?.dueDate && item.dueDate >= prima.data ? item.dueDate : prima.data);
    setData(giorno);
    setOra(mio?.alle ? String(Number(mio.alle.slice(11, 13))) : "");
    setAperto(true);
  };

  const salva = async (chiave) => {
    if (!orarioValido(chiave)) { addToast("Scegli un orario dalla prossima ora piena in poi", "error"); return; }
    setSalvando(true);
    const dati = {
      alle: chiave, titolo: item.title || "(senza titolo)",
      projectId: projectId || item.projectId || null, taskId: taskIdDaAprire || item.id,
      updatedAt: serverTimestamp(),
    };
    try {
      if (mio) await updateDoc(doc(db, "promemoria", mio.id), dati);
      else await addDoc(collection(db, "promemoria"), {
        ...dati, uid, orgId: item.organizationId || null, rifCollezione: collezione, rifId: item.id, createdAt: serverTimestamp(),
      });
      addToast(`🔔 Te lo ricordo ${testoOrario(chiave)}`, "success");
      setAperto(false);
    } catch (e) { addToast("Promemoria non salvato: " + (e?.message || e), "error"); }
    setSalvando(false);
  };

  const togli = async () => {
    if (!mio) return;
    try { await deleteDoc(doc(db, "promemoria", mio.id)); addToast("Promemoria tolto", "info"); setAperto(false); }
    catch (e) { addToast("Promemoria non tolto: " + (e?.message || e), "error"); }
  };

  const ore = oreDisponibili(data);
  const rapide = sceltePrimaDellaScadenza(item?.dueDate, item?.dueTime);
  const btn = { padding: "4px 10px", borderRadius: 6, border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#6d28d9", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter',sans-serif" };

  if (!item?.id || !uid) return null;

  return (
    <div>
      {!aperto && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {mio ? (
            <>
              <span style={{ fontSize: 12.5, color: "#5b21b6", fontWeight: 600 }}>🔔 Te lo ricordo {testoOrario(mio.alle)}</span>
              <button onClick={apri} disabled={disabilitato} style={btn}>Cambia</button>
              <button onClick={togli} disabled={disabilitato} style={{ ...btn, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb" }}>Togli</button>
            </>
          ) : (
            <button onClick={apri} disabled={disabilitato} style={btn}>🔔 Ricordamelo alle…</button>
          )}
        </div>
      )}
      {aperto && (
        <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: 10 }}>
          {rapide.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {rapide.map(s => <button key={s.chiave} disabled={salvando} onClick={() => salva(s.chiave)} style={btn}>{s.etichetta} della scadenza</button>)}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input type="date" value={data} min={primaOraUtile().data} onChange={e => { setData(e.target.value); setOra(""); }}
              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", fontSize: 12.5, fontFamily: "'Inter',sans-serif" }} />
            <select value={ora} onChange={e => setOra(e.target.value)}
              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", fontSize: 12.5, fontFamily: "'Inter',sans-serif" }}>
              <option value="">ora…</option>
              {ore.map(h => <option key={h} value={h}>{p2(h)}:00</option>)}
            </select>
            <button disabled={salvando || ora === "" || !data} onClick={() => salva(chiaveOrario(data, Number(ora)))}
              style={{ ...btn, background: "#7c3aed", color: "#fff", border: "none", opacity: (ora === "" || !data) ? .5 : 1 }}>Imposta</button>
            <button onClick={() => setAperto(false)} style={{ ...btn, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb" }}>Annulla</button>
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, lineHeight: 1.45 }}>
            ℹ️ I promemoria partono allo scoccare dell'ora (es. 15:00), dalla prossima ora piena in poi: per una scadenza tra meno di un'ora non è possibile impostarlo.
            Arrivano nella campanella, come notifica sul dispositivo e per email. Li vedi solo tu.
          </div>
          {data && ore.length === 0 && data < toLocalDateStr(new Date()) && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>Il giorno scelto è già passato.</div>}
        </div>
      )}
    </div>
  );
}
